import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Storefront";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import sidebarSections from "../sidebarConfig";
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from "./layoutConstants";
import { brandColors } from "../../LandingPage.theme";

const railTransition = (theme) =>
  theme.transitions.create(["width", "opacity"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  });

function SidebarContent({
  collapsed,
  currentPath,
  onNavigate,
  onToggleCollapse,
}) {
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: collapsed ? 0 : 2.75,
          justifyContent: collapsed ? "center" : "flex-start",
          height: 68,
          flexShrink: 0,
        }}
      >
        <Avatar
          sx={{
            width: 38,
            height: 38,
            background: `linear-gradient(135deg, ${brandColors.emeraldLight} 0%, ${brandColors.emeraldDark} 100%)`,
            boxShadow: "0 4px 12px rgba(18, 184, 134, 0.45)",
          }}
        >
          <StoreIcon fontSize="small" />
        </Avatar>
        {!collapsed && (
          <Box sx={{ overflow: "hidden" }}>
            <Typography
              variant="subtitle1"
              noWrap
              fontWeight={700}
              sx={{ color: "#fff", lineHeight: 1.2 }}
            >
              Salatty
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: "rgba(255,255,255,0.5)" }}
            >
              Admin Panel
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      <List
        sx={{
          px: collapsed ? 1 : 1.5,
          py: 2,
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {sidebarSections.map((section) => (
          <Box key={section.label} sx={{ mb: 1 }}>
            {!collapsed && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  px: 1.5,
                  pt: 1.5,
                  pb: 0.5,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                }}
              >
                {section.label}
              </Typography>
            )}
            {section.items.map(({ label, path, icon: Icon }) => {
              const isActive = currentPath.startsWith(path);
              const button = (
                <ListItemButton
                  key={path}
                  component={NavLink}
                  to={path}
                  onClick={onNavigate}
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    mb: 0.5,
                    minHeight: 42,
                    justifyContent: collapsed ? "center" : "flex-start",
                    px: collapsed ? 1.25 : 1.5,
                    color: "rgba(255,255,255,0.7)",
                    "&.active": {
                      bgcolor: "rgba(18, 184, 134, 0.2)",
                      color: "#fff",
                      "& .MuiListItemIcon-root": { color: brandColors.emeraldLight },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "22%",
                        bottom: "22%",
                        width: 3,
                        borderRadius: "0 4px 4px 0",
                        bgcolor: "secondary.light",
                      },
                    },
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.06)",
                      color: "#fff",
                    },
                    transition: (theme) =>
                      theme.transitions.create(
                        ["background-color", "color", "padding"],
                        {
                          duration: theme.transitions.duration.shortest,
                        },
                      ),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 38,
                      color: "inherit",
                      justifyContent: "center",
                    }}
                  >
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      slotProps={{
                        primary: {
                          fontSize: 13.5,
                          fontWeight: isActive ? 600 : 500,
                        },
                      }}
                    >
                      {label}
                    </ListItemText>
                  )}
                </ListItemButton>
              );

              return collapsed ? (
                <Tooltip key={path} title={label} placement="right" arrow>
                  {button}
                </Tooltip>
              ) : (
                button
              );
            })}
          </Box>
        ))}
      </List>

      {onToggleCollapse && (
        <>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
          <Tooltip
            title={collapsed ? "Expand sidebar" : ""}
            placement="right"
            arrow
          >
            <Box
              onClick={onToggleCollapse}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                justifyContent: collapsed ? "center" : "flex-start",
                px: collapsed ? 0 : 2.5,
                py: 1.75,
                mx: 1,
                my: 0.75,
                borderRadius: 2,
                cursor: "pointer",
                color: "rgba(255,255,255,0.55)",
                flexShrink: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.06)", color: "#fff" },
                transition: (theme) =>
                  theme.transitions.create(["background-color", "color"]),
              }}
            >
              {collapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )}
              {!collapsed && (
                <Typography variant="body2" fontWeight={600} noWrap>
                  Collapse menu
                </Typography>
              )}
            </Box>
          </Tooltip>
        </>
      )}
    </Box>
  );
}

export default function AdminSidebar({
  mobileOpen,
  onCloseMobile,
  collapsed,
  onToggleCollapse,
}) {
  const { pathname } = useLocation();

  const desktopWidth = collapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box
      component="nav"
      sx={{ width: { md: desktopWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile: temporary overlay drawer, always shows full content */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onCloseMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            bgcolor: brandColors.inkDark,
            backgroundImage: `linear-gradient(180deg, ${brandColors.inkDark} 0%, ${brandColors.ink} 100%)`,
          },
        }}
      >
        <SidebarContent
          collapsed={false}
          currentPath={pathname}
          onNavigate={onCloseMobile}
        />
      </Drawer>

      {/* Desktop: permanent drawer that shrinks to an icon rail instead of disappearing */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: desktopWidth,
            boxSizing: "border-box",
            bgcolor: brandColors.inkDark,
            backgroundImage: `linear-gradient(180deg, ${brandColors.inkDark} 0%, ${brandColors.ink} 100%)`,
            border: "none",
            overflowX: "hidden",
            transition: railTransition,
          },
        }}
      >
        <SidebarContent
          collapsed={collapsed}
          currentPath={pathname}
          onNavigate={() => {}}
          onToggleCollapse={onToggleCollapse}
        />
      </Drawer>
    </Box>
  );
}
